// Portal SPA entry point. Registers routes, wires the auth guard, boots the router.
import { router } from './router/router.js';
import { session } from './api/session.js';
import { bootstrapSession, setSessionExpiredHandler } from './api/http.js';
import { renderLogin, renderJoin } from './views/login.js';
import { renderDashboard } from './views/dashboard.js';
import { renderProfiles, renderProfileDetail } from './views/profiles.js';
import { renderFields } from './views/fields.js';
import { renderLinks } from './views/links.js';
import { renderSubscribers } from './views/subscribers.js';
import { renderApprovals } from './views/approvals.js';
import { renderContacts } from './views/contacts.js';
import { renderCode } from './views/code.js';
import { renderSettings } from './views/settings.js';
import { renderPublic, renderSubscribe, renderInvite } from './views/public.js';
import { parseInvitePath, hashForInvite } from './router/deeplink.js';
import { h, toast } from './components/dom.js';
import { ensureTimeZoneInitialized } from './lib/timezone.js';

// Public (no-auth) routes — reachable while logged out.
const PUBLIC = new Set(['/login', '/join']);
const isPublicPrefix = (p: string) =>
  PUBLIC.has(p) || p.startsWith('/public/') || p.startsWith('/subscribe') || p.startsWith('/id/');

// Auth guard: unauthenticated users are bounced to /login (unless the route is public).
// We treat `hadSession` as "attempt silent renew" — the API layer handles 401→renew, so we
// optimistically allow through and let a failed call redirect. Hard gate only when no hint.
router.setBeforeEach((path: string) => {
  const [p] = path.split('?');
  if (isPublicPrefix(p)) return null;
  if (session.isLoggedIn || session.hadSession) return null;
  // Stash the intended deep-link so login can return here after auth. Skip the
  // dashboard (no point returning to '/') and never overwrite a pending value.
  if (path && path !== '/' && !sessionStorage.getItem('vid_return_to')) {
    try { sessionStorage.setItem('vid_return_to', path); } catch { /* ignore */ }
  }
  return '/login';
});

router
  .add('/login', renderLogin)
  .add('/join', renderJoin)
  .add('/', renderDashboard)
  .add('/profiles', renderProfiles)
  .add('/profiles/:id', renderProfileDetail)
  .add('/fields', renderFields)
  .add('/links', renderLinks)
  .add('/subscribers', renderSubscribers)
  .add('/approvals', renderApprovals)
  .add('/contacts', renderContacts)
  // Authed deep-link to a single contact's card (e.g. virtualid.one/contacts/<handle>).
  // Reuses the contacts view, pre-filtered to just this person via the existing
  // id:<handle> scoped search. Behind the auth guard like the rest of /contacts:
  // logged-out visitors are bounced to /login first, then land back here.
  .add('/contacts/:handle', (params, _query) =>
    renderContacts(params, new URLSearchParams({ q: 'id:' + (params.handle || ''), focus: params.handle || '' })))
  .add('/code', renderCode)
  .add('/settings', renderSettings)
  .add('/subscribe', renderSubscribe)
  // PATH deep-links (/id/...) are translated to these hash routes on boot (see below).
  .add('/id/:alias/invite/:code', renderInvite)
  .add('/id/:alias/subscribe', renderSubscribe)
  .add('/id/:alias', renderPublic)
  .add('/public/:alias', renderPublic);

router.setNotFound(() => {
  document.getElementById('app')!.replaceChildren(
    h('div', { style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px' },
      h('h1', {}, '404'),
      h('p', { class: 'muted' }, 'Nothing here.'),
      h('a', { class: 'btn btn-primary', href: '#/' }, 'Go to dashboard')));
});

// Global error surfacing for unhandled promise rejections (dev aid).
window.addEventListener('unhandledrejection', (e) => {
  console.error('[portal] unhandled', e.reason);
});

// When an authed request hits an unrecoverable 401 (session expired, renewal failed),
// the HTTP layer calls this to bounce the user to /login immediately — no manual refresh
// needed. Uses the router so the SPA navigates cleanly; toasts a heads-up.
setSessionExpiredHandler(() => {
  toast('Your session expired — please sign in again.', 'warn');
  location.hash = '#/login';
});

// On reload, silently re-establish the session (persisted DPoP key + refresh)
// BEFORE starting the router, so authed routes render without a login bounce.
await bootstrapSession();

// One-time: seed the display timezone from the browser if the user has never
// chosen one, so the first authed render already shows dates in a real zone.
ensureTimeZoneInitialized();

// PATH deep-link support: Caddy serves index.html for /id/* (SPA fallback), so a printed
// link like /id/alex-kim/invite/BIZ-7Q2K lands here with a real pathname (NOT a hash).
// Translate it into the equivalent in-app hash route before the router starts, preserving
// any ?token= fallback. This keeps the router hash-based while URLs stay human-meaningful.
(() => {
  const parsed = parseInvitePath(location.pathname);
  if (parsed) {
    const target = hashForInvite(parsed, location.search);
    // Reset the path to '/' so reloads/back don't re-trigger, then set the hash route.
    history.replaceState(null, '', '/' + target);
  }
})();

router.start();
